import React, { useMemo } from "react";
import { Mention, MentionsInput, OnChangeHandlerFunc } from "react-mentions";

import { ComponentSettingsVariables } from "@intelligems/ig-widgets/ProgressBarView/util/textVariables";
import { Grid, MenuItem } from "@mui/material";
import { ExternalVariableTextInputMenu } from "@src/features/personalizations/components/offerModifications/userInterfaces/ExternalVariableTextInputMenu";
import { useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { theme } from "@src/theme";

import { fromMentionsVariables, toMentionsVariables } from "./utils";

interface VariableTextInputProps {
  variables?: ComponentSettingsVariables[];
  value: string;
  limit?: number;
  placeholder?: string;
  onChange?: (value: string) => void;
}

export const VariableTextInput = ({
  variables = [],
  limit,
  value,
  placeholder,
  onChange,
}: VariableTextInputProps) => {
  const experience = useSelector(selectedExperience);
  const formatted = useMemo(
    () => toMentionsVariables(value, variables),
    [value, variables]
  );

  const data = useMemo(
    () =>
      variables.map((v) => ({
        category: v.category,
        id: "#" + v.id,
        display: v.display,
      })),
    [variables]
  );

  const handleChange: OnChangeHandlerFunc = (e) => {
    if (onChange) {
      onChange(fromMentionsVariables(e.target.value, variables));
    }
  };

  const renderSuggestion = (
    suggestion,
    search,
    highlightedDisplay,
    index,
    focused
  ) => {
    return <MenuItem selected={focused}>{suggestion.display}</MenuItem>;
  };

  return (
    <Grid item container direction={"row"} wrap={"nowrap"} columnSpacing={2}>
      <Grid item xs={12}>
        {/* @ts-ignore */}
        <MentionsInput
          autoFocus={false}
          maxLength={limit}
          className={"ig-mentions-input"}
          style={{
            control: {
              fontSize: "14px",
              width: "100%",
              borderRadius: "6px",
            },
            "&multiLine": {
              control: {
                fontFamily: theme.typography.fontFamily,
                minHeight: 40,
              },
              highlighter: {
                padding: "10px 12px",
                border: "1px solid transparent",
              },
              input: {
                padding: "10px 12px",
                border: `1px solid ${theme.igBorders.core.default}`,
                borderRadius: "6px",
              },
            },
            suggestions: {
              list: {
                backgroundColor: theme.igBackgroundColors.baseLightLow,
                border: `1px solid ${theme.igBorders.core.default}`,
                fontSize: "14px",
                borderRadius: 6,
              },
              item: {
                fontFamily: theme.typography.fontFamily,
                padding: "5px 15px",
                // borderBottom: "1px solid rgba(0,0,0,0.15)",
                "&focused": {
                  backgroundColor: "#F1F1F1",
                },
              },
            },
          }}
          disabled={experience?.viewMode === "view" || false}
          value={formatted}
          onChange={handleChange}
          placeholder={placeholder}
        >
          {/* @ts-ignore */}
          <Mention
            data={data}
            trigger="{{"
            displayTransform={(id, display) => `{{${display}}}`}
            renderSuggestion={renderSuggestion}
            style={{
              position: "relative",
              textShadow:
                "1px 1px 1px white, 1px -1px 1px white, -1px 1px 1px white, -1px -1px 1px white",
              zIndex: 1,
              pointerEvents: "none",
              color: theme.palette.text.primary,
            }}
          />
        </MentionsInput>
      </Grid>
      {onChange && (
        <Grid item mt={1}>
          <ExternalVariableTextInputMenu
            variables={variables}
            onChange={onChange}
            formatted={formatted}
          />
        </Grid>
      )}
    </Grid>
  );
};
